// ─────────────────────────────────────────────────────────────────────────────
// Aidey Feature Guides: THE ONE REGISTRY of guided tours.
// Each key matches a tourId used by useAutoTour() and the ? beside a page title (HelpButton, or
// PageHeader's `help`). Every tour runs on the shared spotlight guide, ui/Guide.jsx (Block M, UI-23).
//
// Step: {
//   title, body   plain text, British English, no em dashes
//   target        the data-tour-id the step points at, or null for a step about the whole page
//                 (the card is then centred, with nothing outlined)
//   whenMissing   only for a target that is legitimately absent for some people or screens:
//                 'skip'   the step is about something this person cannot see (Plan and billing for
//                          a member), so it is left out of their tour
//                 'centre' the step still applies, so it is shown centred (the chat history is its
//                          own screen on a phone)
//                 Without it, a missing target is a defect: tourTargets.test.js fails when no page
//                 source writes the id, and scripts/preview/tours-check.js when it does not render.
//   points        the quoted control the body tells the person to tap (e.g. '+ New invoice'): the target
//                 IS that control; tours-check.js (E) fails if the outlined element is not named it
//   mentions      quoted controls the body names in passing (on another page, or inside the region the
//                 step outlines); every quoted "tap/click/choose/use" control is in points or mentions
//                 (tourStepsPoint.test.js). A step with no points that outlines a control must name it.
// }
//
// Block M (2026-10-12): the targets were moved here from tours/tourContent.js, a second registry that
// nothing imported (the tours that ran had no targets at all, so nothing was ever outlined). Steps
// whose subject is no longer on the page were rewritten to what the page has now; each is marked
// "Block M: rewritten" with the reason.
// ─────────────────────────────────────────────────────────────────────────────

export const allGuides = {

  // ── DASHBOARD ──────────────────────────────────────────────────────────────
  dashboard: {
    steps: [
      {
        title: 'Your Dashboard',
        body: 'This is your starting point each day. It pulls together your tasks, invoices, calendar, and client activity so you can see where things stand at a glance.',
        target: null,
      },
      {
        // Block M: rewritten. The row of summary cards (active tasks, unpaid invoices, meetings) is
        // gone; the first row is now the briefing beside Workload.
        title: 'Today at a glance',
        body: 'The first row shows your day: Aidey\'s briefing beside your workload, so you can see how busy today is before you start.',
        target: 'dashboard-stats',
      },
      {
        // Block M: rewritten. The briefing is a morning plan and, later on, an evening wrap-up.
        title: 'Your briefing',
        body: 'Aidey writes a short briefing from your calendar, tasks, and invoices: a plan for the day in the morning, and a wrap-up of what got done in the evening.',
        target: 'dashboard-briefing',
      },
      {
        // Block M: rewritten. The week view is gone; its place is Top tasks.
        title: 'Top tasks',
        body: 'Your most important tasks, sorted by priority or by time. Tick one off here when it is done, or tap it to open it.',
        target: 'dashboard-week',
      },
      {
        title: 'Aidey',
        body: 'Tap Aidey in the corner to talk to it. You can ask it to create a task, draft an email, look up an invoice, or anything else related to your work.',
        target: 'dashboard-chat-bubble',
      },
    ],
  },

  // ── CRM ────────────────────────────────────────────────────────────────────
  crm: {
    steps: [
      {
        title: 'Your CRM Pipeline',
        body: 'This board tracks every client and prospect through your sales process. Each column is a stage, and each card is a contact.',
        target: 'crm-kanban-board',
      },
      {
        title: 'Moving contacts',
        body: 'Drag a contact card left or right to move it to another stage, or use the card\'s menu and choose Move to stage. The stage is saved to the contact record straight away.',
        target: 'crm-kanban-board',
      },
      {
        title: 'Adding a contact',
        body: 'Tap "+ Add contact" at the top to create a new contact. They appear in the first column of your pipeline automatically.',
        target: 'crm-add-contact',
        points: '+ Add contact',
        whenMissing: 'skip',   // shown only once the board has stages
      },
      {
        title: 'Opening a contact',
        body: 'Tap any card to open the full contact record. Inside you will find interaction history, linked invoices, health score, and notes.',
        target: 'crm-contact-card',
        whenMissing: 'centre',   // an empty board has no card yet; the step still applies
      },
      {
        // Block M: rewritten. The badge is no longer red, amber and green: it is a dot, the score
        // and a word, and only clients carry one.
        title: 'Health Score',
        body: 'A client\'s card shows their health score: a number and a word, from Excellent to At risk. It reflects recent contact, open work, and invoice status.',
        target: 'crm-health-badge',
        whenMissing: 'centre',   // a board with no clients on it shows no score yet
      },
    ],
  },

  // ── PROJECTS ───────────────────────────────────────────────────────────────
  projects: {
    steps: [
      {
        title: 'Your Projects',
        body: 'All active projects live here, both client work and internal projects. The tiles at the top show your active projects and any overdue tasks across all of them.',
        target: 'projects-stats',
      },
      {
        title: 'Opening a project',
        body: 'Tap any project card or row to open it and see its tasks, time, and money in one place.',
        target: 'projects-list',
        whenMissing: 'centre',   // no projects yet
      },
      {
        title: 'Creating a project',
        body: 'Tap "+ New project" to create one. You can start from a blank project or pick a workflow template that sets up the task structure for you.',
        target: 'projects-new-project',
        points: '+ New project',
      },
      {
        // Block M: rewritten to the controls the page has (Cards or List; sort by status, name or due date).
        title: 'Switching views',
        body: 'Show your projects as cards or as a list. In the list you can sort by status, name, or due date.',
        target: 'projects-view-toggle',
      },
    ],
  },

  // ── INVOICING ──────────────────────────────────────────────────────────────
  invoicing: {
    steps: [
      {
        // Block M: rewritten. There is no left-hand menu and no Quotes; the sections are tabs.
        title: 'Invoicing and Finance',
        body: 'This section covers invoices, purchase orders, expenses, your tax summary, and transactions. Use the tabs to switch between them.',
        target: 'invoicing-nav',
        whenMissing: 'skip',   // UI-24 item 19: Basics has the Invoices tab only (no tabs, no Purchase orders or Expenses)
      },
      {
        title: 'Creating an invoice',
        body: 'Go to Invoices and tap "+ New invoice". Fill in the client, line items, and due date, then send it to your client directly from Aidey.',
        // Carla's live check (2026-10-12): this outlined the Invoices tab; it points at the button it names.
        target: 'invoicing-new-invoice',
        points: '+ New invoice',
      },
      {
        // Block M: rewritten. Quotes is not a section any more; Purchase orders is.
        title: 'Purchase orders',
        body: 'Go to Purchase orders to raise an order with a supplier and keep track of what has been ordered and paid.',
        target: 'invoicing-nav-purchase-orders',
        whenMissing: 'skip',   // UI-24 item 19: Basics has the Invoices tab only (no tabs, no Purchase orders or Expenses)
      },
      {
        title: 'Expenses',
        body: 'Log business costs under Expenses. Categorise them and mark them as billable so they can be added to a client invoice later.',
        target: 'invoicing-nav-expenses',
        whenMissing: 'skip',   // UI-24 item 19: Basics has the Invoices tab only (no tabs, no Purchase orders or Expenses)
      },
      {
        title: 'Tracking payment',
        body: 'Each invoice shows its status, such as Draft, Sent, or Paid. Tap an invoice to mark it paid or to send a reminder.',
        target: null,
      },
    ],
  },

  // ── SCHEDULER / BOOKING PAGES ──────────────────────────────────────────────
  scheduler: {
    steps: [
      {
        title: 'Client Booking Pages',
        body: 'The Scheduler lets clients book time with you without back-and-forth emails. You share a link and they pick a slot that fits your availability.',
        target: null,
      },
      {
        title: 'Creating a meeting type',
        body: 'Tap "+ New meeting type" to set up a bookable meeting. Give it a name, set the duration, and add any questions you want clients to answer when they book.',
        target: 'scheduler-new-type',
        points: '+ New meeting type',
      },
      {
        title: 'Your booking link',
        body: 'Your booking page has its own link. Copy it from here and paste it into your email signature, website, or anywhere clients can see it.',
        target: 'scheduler-booking-link',
      },
      {
        title: 'Toggling availability',
        body: 'You can pause any meeting type to stop new bookings without deleting it. Use the switch on its card to turn it off or on.',
        target: 'scheduler-types-list',
      },
      {
        // Block J (2026-10-12): the account's default hours moved here from the Scheduling hub,
        // which had no way in (its own tour, 'scheduling', went with it).
        title: 'Your bookable hours',
        body: 'Tap "Availability" to set the hours clients can book you, the time kept free around each meeting, and how much notice a booking needs. Every meeting type uses these unless it has its own.',
        target: 'scheduler-availability-tab',
        points: 'Availability',
      },
    ],
  },

  // ── HR / TEAM ──────────────────────────────────────────────────────────────
  hr: {
    steps: [
      {
        title: 'HR and Team Management',
        body: 'This is your HR hub. It shows every team member, their current status, and any alerts that need your attention.',
        target: null,
      },
      {
        // Block M: rewritten. There is no Alerts column; each card lists its alerts, and the filter
        // narrows the cards to the people who need something.
        title: 'Team alerts',
        body: 'Each card flags what needs action, such as a probation period ending, an expiring document, or a leave request to approve. Choose "Needs attention" to see only those people.',
        // It tells the person to choose "Needs attention", so it outlines that choice, not the whole toolbar.
        target: 'hr-needs-attention',
        points: 'Needs attention',
      },
      {
        title: 'Opening a profile',
        body: 'Tap View profile on a card to open their full HR profile. Inside you will find their details, leave, documents, training, and reviews.',
        target: 'hr-member-card',
        whenMissing: 'centre',   // no one in the team yet
      },
      {
        // Block M: rewritten. Leave requests are not listed on this page; they are approved in the
        // person's profile, on its Leave tab.
        title: 'Leave requests',
        body: 'A card shows when someone has leave waiting for you. Open their profile and go to Leave to see the dates, then approve or decline.',
        target: null,
      },
    ],
  },

  // ── EXPENSES ───────────────────────────────────────────────────────────────
  expenses: {
    steps: [
      {
        title: 'Expenses',
        body: 'Log and track every business expense here. Everything you add feeds into your financial overview and can be linked to a client invoice.',
        target: null,
      },
      {
        title: 'Adding an expense',
        body: 'Tap "+ New expense" to log a cost. Enter the amount, category, supplier, and date. Tick "Billable" if you plan to charge it back to a client.',
        target: 'expenses-new',
        points: '+ New expense',
        mentions: ['Billable'],   // on the form the button opens
      },
      {
        title: 'Filtering expenses',
        body: 'Use the filter bar to narrow your expenses down. This is useful when preparing for tax returns or checking spend by area.',
        target: 'expenses-filter',
        whenMissing: 'centre',   // not drawn while the list is loading or failed
      },
      {
        title: 'Adding to an invoice',
        body: 'Any expense marked billable can be pulled into a client invoice when you create one.',
        target: null,
      },
    ],
  },

  // ── SETTINGS ───────────────────────────────────────────────────────────────
  settings: {
    steps: [
      {
        title: 'Settings',
        body: 'Settings is where you configure Aidey to work the way you do. Start with your profile, then connect your email and calendar.',
        target: null,
      },
      {
        title: 'Profile',
        body: 'Go to Profile and fill in your name, company details, and communication preferences. Aidey uses these to personalise its suggestions and drafts.',
        target: 'settings-nav-profile',
      },
      {
        title: 'Email connections',
        body: 'Go to Email connections to link your Gmail or Outlook account. Once connected, Aidey can read your inbox, draft replies, and action emails on your behalf.',
        target: 'settings-nav-email',
      },
      {
        // Block M: rewritten. Video has its own section now, just below Calendar.
        title: 'Calendar and video',
        body: 'Connect your calendar under Calendar so Aidey knows your availability. Zoom and Google Meet are under Video, just below it.',
        target: 'settings-nav-calendar',
      },
      {
        title: 'Plan and billing',
        body: 'See your current plan, usage limits, and billing details under Plan and billing. Upgrade or change your plan from here at any time.',
        target: 'settings-nav-billing',
        whenMissing: 'skip',   // the owner and admins only (adminOnly in SettingsLayout)
      },
    ],
  },

  // ── AIDEY CHAT ─────────────────────────────────────────────────────────────
  'aidey-chat': {
    steps: [
      {
        title: 'Aidey',
        body: 'Aidey is your AI assistant. Ask it anything about your work and it will answer, create, or take action on your behalf.',
        target: null,
      },
      {
        title: 'Starting a conversation',
        body: 'Type your message in the "Message Aidey" box at the bottom and tap Send. Try "What tasks are overdue?" or "Draft a follow-up email to Sarah at Acme."',
        target: 'chat-input',
        points: 'Message Aidey',
      },
      {
        title: 'What Aidey can do',
        body: 'Aidey can create tasks, schedule meetings, pull invoice totals, draft emails, summarise your week, and look things up across your account. Give it as much context as you can.',
        target: null,
      },
      {
        title: 'Chat history',
        body: 'Previous conversations are saved in your chat history. Tap any conversation to continue it, or tap "+ New chat" to start fresh.',
        target: 'chat-history',
        mentions: ['+ New chat'],   // the step is about the history list it outlines
        whenMissing: 'centre',   // on a phone the history is its own screen
      },
    ],
  },

  // ── WORKFLOW TEMPLATES (Settings) ──────────────────────────────────────────
  // Block M: rewritten. This tour runs on Settings > Workflow templates, a list of templates; it
  // described the stage editor, which appears only once a template is opened for editing.
  workflows: {
    steps: [
      {
        title: 'Workflow templates',
        body: 'Workflow templates are ready-made sets of stages and columns for your projects and CRM boards. Pick one when you create a project or a board.',
        target: null,
      },
      {
        title: 'Aidey templates',
        body: 'Aidey\'s own templates cover common setups and are ready to use. They cannot be changed, so they are always there to start from.',
        target: 'workflows-projects',
      },
      {
        title: 'Your templates',
        body: 'Set up a project or CRM board the way you like it, then use "Save as template" on its Workflow tab. It appears here under Your templates.',
        target: 'workflows-crm',
        mentions: ['Save as template'],   // on a project's or board's Workflow tab, not on this page
      },
      {
        title: 'Editing stages',
        body: 'Tap Edit on one of your templates to rename, reorder, or add stages and columns, then save. Delete removes a template you no longer need.',
        target: null,
      },
    ],
  },

  // ── FAMILY ─────────────────────────────────────────────────────────────────
  family: {
    steps: [
      { title: 'Your household',
        body: 'This is where everyone in your home lives inside Aidey. Add a person for each member of the family, and Aidey can keep track of what belongs to who.',
        target: null },
      { title: 'Adding someone',
        body: 'Tap Add member to add a person. You can note their birthday, any allergies, and who their doctor or dentist is, so it is all in one place when you need it.',
        target: 'family-add-member' },
      { title: 'Logins for children',
        body: 'Children can have their own login with a simple username. They see a smaller, safer version of Aidey with their own jobs and stars.',
        target: null },
      { title: 'Stars and rewards',
        body: 'Set up rewards that children can earn by doing their jobs. They collect stars, and when they have enough they can swap them for a reward you have chosen.',
        target: null },
      { title: 'Keeping it separate',
        body: 'Your family things stay in your family space. Work never mixes in, and work never sees any of this.',
        target: null },
    ],
  },

  // ── SHOPPING ───────────────────────────────────────────────────────────────
  shopping: {
    steps: [
      { title: 'Your shopping lists',
        body: 'Make a list for anything you like. A weekly food shop, a birthday party, bits for the house. You can have as many lists as you want.',
        target: 'shopping-lists' },
      { title: 'Adding things',
        body: 'Type what you need and add it to the list. You can put a number next to it too, like six eggs or two loaves.',
        target: 'shopping-add-item',
        whenMissing: 'centre' },   // shown once a list is open
      { title: 'Ticking things off',
        body: 'Tap an item when it is in the trolley and it gets crossed off. Tap it again if you change your mind.',
        target: null },
      { title: 'Your own lists',
        body: 'Your lists are yours. Everyone in the house keeps their own, so nothing gets muddled up or ticked off by mistake.',
        target: null },
      { title: 'Ask Aidey',
        body: 'You can just tell Aidey what you need instead of typing it in here. Say add bread and bin bags to the shopping list and it will do it for you.',
        target: 'dashboard-chat-bubble' },
    ],
  },

  // ── BUDGET ─────────────────────────────────────────────────────────────────
  budget: {
    steps: [
      { title: 'Your money in one place',
        body: 'This is where you keep an eye on what comes in and what goes out, so nothing takes you by surprise.',
        target: null },
      { title: 'What you spend',
        body: 'Tap "+ Add income or bill" to add the things you pay for regularly. Aidey works out what is left over once they are all paid.',
        target: 'budget-add', points: '+ Add income or bill' },
      { title: 'Saving up',
        body: 'Set up a savings goal for something you are working towards. Add money to it whenever you can and watch it grow.',
        target: null },
      { title: 'Safe to spend',
        body: 'Aidey looks at what is still due before your next payday and tells you what you can safely spend without running short.',
        target: null },
      { title: 'Private to you',
        body: 'Your money is in your personal space. Nobody at work can see any of it.',
        target: null },
    ],
  },

  // ── MEAL PLANNER ───────────────────────────────────────────────────────────
  mealplanner: {
    steps: [
      { title: 'What is for dinner',
        body: 'Plan your meals for the week so you are not stood staring in the fridge at six o clock.',
        target: null },
      { title: 'Filling in the week',
        body: 'Tap any day to add a meal. Tap it again to change it, or clear it if plans change.',
        target: 'mealplanner-week',
        whenMissing: 'centre' },   // not drawn while the week is loading
      { title: 'Stuck for ideas',
        body: 'Tell Aidey what is in your cupboards and it will suggest something you can actually make. It knows about any allergies in your household.',
        target: 'dashboard-chat-bubble' },
      { title: 'Straight to the list',
        body: 'Once you know what you are cooking, ask Aidey to put what you need on the shopping list.',
        target: null },
    ],
  },

  // ── RENEWALS ───────────────────────────────────────────────────────────────
  renewals: {
    steps: [
      { title: 'Never get caught out',
        body: 'Keep track of anything that renews. Insurance, your MOT, subscriptions, your passport. Aidey will tell you before it happens.',
        target: null },
      { title: 'Adding one',
        body: 'Tap Add renewal and fill in what it is, when it is due, and how much notice you want. Aidey handles the rest.',
        target: 'renewals-add' },
      { title: 'Health check ups too',
        body: 'Use the Health category for dentist and optician appointments. You can set these to repeat every two, three or five years, which suits the ones that come round slowly.',
        target: null },
      { title: 'What it all costs',
        body: 'The card at the top adds up what your renewals cost you over a year, so you can see it all in one number.',
        target: null },
      { title: 'Who it is for',
        body: 'You can say who a renewal belongs to. Handy when two people in the house both have a car or a phone contract.',
        target: null },
    ],
  },

  // ── BIRTHDAYS ──────────────────────────────────────────────────────────────
  birthdays: {
    steps: [
      { title: 'Birthdays and anniversaries',
        body: 'Put in the dates you do not want to forget and Aidey will remind you in good time.',
        target: null },
      { title: 'Adding a date',
        body: 'Tap "+ Add birthday", then add the person and the date. If you know the year they were born, Aidey can work out how old they are turning.',
        target: 'birthdays-add', points: '+ Add birthday' },
      { title: 'Time to buy a card',
        body: 'You get a nudge before the day, not on the morning of it, so there is still time to sort a present.',
        target: null },
      { title: 'Everyone in the family',
        body: 'Add dates for anyone, not just people in your household. Grandparents, friends, whoever matters.',
        target: null },
    ],
  },

  // ── JOURNAL ────────────────────────────────────────────────────────────────
  journal: {
    steps: [
      { title: 'Your journal',
        body: 'A private place to write things down. How your day went, something you want to remember, anything at all.',
        target: null },
      { title: 'Writing an entry',
        body: 'Start a new entry and write as much or as little as you like. You can add a photo too.',
        target: 'journal-new',
        whenMissing: 'centre' },   // on a phone it is inside the Categories drawer
      { title: 'Keeping it tidy',
        body: 'Sort your entries into categories so you can find things again later.',
        target: null },
      { title: 'Only you',
        body: 'Your journal is private. Nobody else can read it, not even other people in your household.',
        target: null },
    ],
  },

  // ── FAMILY CHAT ────────────────────────────────────────────────────────────
  familychat: {
    steps: [
      { title: 'Family chat',
        body: 'A place for everyone at home to talk. Ask what time tea is, say you are running late, share the good news first.',
        target: 'familychat-compose' },
      { title: 'Everyone is here',
        body: 'Everyone in your household can see and join in, including children with their own logins.',
        target: null },
      { title: 'Separate from work',
        body: 'This is only for your family. Nothing here ever shows up on the work side.',
        target: null },
    ],
  },

};

// INTRO-2: append the help-button reminder as a final line to the LAST step of EVERY
// tour, so every walkthrough ends by pointing the user at the ? button. Applied here
// over allGuides so every tour (and any added later) carries it, with no per-tour edit
// to drift out of sync. Idempotent. Block M: the ? sits beside the page title (decision 86).
export const HELP_REMINDER =
  'One last thing: you can reopen this walkthrough at any time by tapping the ? next to the page title.';

for (const key of Object.keys(allGuides)) {
  const steps = allGuides[key] && allGuides[key].steps;
  if (Array.isArray(steps) && steps.length) {
    const last = steps[steps.length - 1];
    if (last && typeof last.body === 'string' && !last.body.includes(HELP_REMINDER)) {
      last.body = `${last.body}\n\n${HELP_REMINDER}`;
    }
  }
}
